function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { Card, Pending, Empty } from "../../components/analytics-kit";
import GeoMap from "../../components/geo-map";
import { getGeoCoverage, getGeoDealers, getGeoHeat, getGeoStores } from "./sales-api";

/*
 * One self-loading map card for the Territory pages (SALES_TERRITORY_MAPS.md §6.2):
 *   Territory overview  dealers + heat (the G3 / G5 charts from the July spec)
 *   Rep detail          coverage filtered to the rep plus the rep's dealers
 *   Market detail       dealer pins for the state plus the state's ZIP3 heat
 *   Stores roster       store pins (G2)
 * Renders nothing at all while the geo rail is not deployed or has no rows for
 * the scope, so the pages read exactly as before until ZIP data exists.
 *
 * props: range · rep · region · basis (bill | ship; dealer pins only, heat is always ship-to) · want {coverage, dealers, heat, stores} · title · hint · span · height · onDealer · onStore · onRep · onHeat
 */
export default class SalesMapCard extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      coverage: undefined,
      dealers: undefined,
      heat: undefined,
      stores: undefined
    });
  }
  componentDidMount() {
    this._alive = true;
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  componentDidUpdate(prev) {
    if (prev.range !== this.props.range || prev.rep !== this.props.rep || prev.region !== this.props.region || prev.basis !== this.props.basis) this.load();
  }
  load() {
    const {
      range,
      rep,
      region,
      basis,
      want = {}
    } = this.props;
    const set = patch => this._alive && this.setState(patch);
    this.setState({
      coverage: undefined,
      dealers: undefined,
      heat: undefined,
      stores: undefined
    });
    // On a market page keep the state's hexes and list only the reps who own ZIPs there.
    const inRegion = cov => {
      if (!cov || !region) return cov;
      const st = String(region).toUpperCase();
      const hexes = cov.hexes.filter(h => !h.state || h.state === st);
      // The rail's legend is the national top reps; on a market page list every rep owning ZIPs here,
      // counted from the state's own hexes, keeping the national colour when the rail gave one.
      const counts = new Map();
      hexes.forEach(h => (h.reps || []).forEach(r => {
        const c = counts.get(r.repCode) || {
          repCode: r.repCode,
          name: r.name || r.repCode,
          zips: 0
        };
        c.zips += Number(r.zips) || Number(h.zips) || 1;
        counts.set(r.repCode, c);
      }));
      const national = new Map((cov.legend || []).map(l => [l.repCode, l]));
      const legend = [...counts.values()].sort((a, b) => b.zips - a.zips).map(c => ({
        ...c,
        name: (national.get(c.repCode) || {}).name || c.name,
        color: (national.get(c.repCode) || {}).color || ""
      }));
      return {
        ...cov,
        hexes,
        legend
      };
    };
    if (want.coverage) getGeoCoverage({
      rep
    }).then(coverage => set({
      coverage: inRegion(coverage)
    })).catch(() => set({
      coverage: null
    }));else set({
      coverage: null
    });
    if (want.dealers) getGeoDealers(range, {
      rep,
      region,
      basis
    }).then(dealers => set({
      dealers
    })).catch(() => set({
      dealers: null
    }));else set({
      dealers: null
    });
    if (want.heat) getGeoHeat(range).then(heat => set({
      heat: heat && region ? heat.filter(z => z.state === String(region).toUpperCase()) : heat
    })).catch(() => set({
      heat: null
    }));else set({
      heat: null
    });
    if (want.stores) getGeoStores(range).then(stores => set({
      stores
    })).catch(() => set({
      stores: null
    }));else set({
      stores: null
    });
  }
  render() {
    const {
      coverage,
      dealers,
      heat,
      stores
    } = this.state;
    const {
      title = "Map",
      hint,
      span = 12,
      height = 380,
      onDealer,
      onStore,
      onRep,
      onHeat,
      want = {},
      right,
      foot,
      always,
      emptyText,
      minStores
    } = this.props;
    const pending = [coverage, dealers, heat, stores].some(x => x === undefined);
    if (pending) return this.props.silentLoading && !always ? null : /*#__PURE__*/React.createElement(Card, {
      title: title,
      hint: hint,
      span: span,
      right: right
    }, /*#__PURE__*/React.createElement(Pending, {
      h: 120
    }));
    const cov = coverage && coverage.hexes.length ? coverage : null;
    const dl = dealers && dealers.length ? dealers : null;
    const ht = heat && heat.length ? heat : null;
    const st = stores && stores.length ? stores : null;
    // A store map needs at least minStores stores with a physical location (a web storefront has none).
    if (minStores && (stores ? stores.length : 0) < minStores) return null;
    if (!cov && !dl && !ht && !st) {
      if (!always) return null;
      return /*#__PURE__*/React.createElement(Card, {
        title: title,
        hint: hint,
        span: span,
        right: right
      }, /*#__PURE__*/React.createElement(Empty, null, emptyText || "Nothing to map yet. Pins need ship-to ZIPs on the customer feed and the nightly geocode; coverage needs ZIP assignments (Manage Data, Territories); heat needs tracker sessions with postal geo."), foot ? /*#__PURE__*/React.createElement("p", {
        className: "wa-foot"
      }, foot) : null);
    }
    return /*#__PURE__*/React.createElement(Card, {
      title: title,
      hint: hint,
      span: span,
      right: right
    }, /*#__PURE__*/React.createElement(GeoMap, {
      height: height,
      coverage: cov,
      dealers: dl,
      heat: ht,
      stores: st,
      defaultLayers: {
        coverage: !!cov,
        dealers: !!dl,
        heat: !!ht && !dl && !cov ? true : !!want.heatOn,
        stores: !!st
      },
      onDealer: onDealer,
      onStore: onStore,
      onRep: onRep,
      onHeat: onHeat
    }), foot ? /*#__PURE__*/React.createElement("p", {
      className: "wa-foot"
    }, foot) : null);
  }
}